import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { getAdvanceLeaves, updateLeaveStatus } from '../../../Redux/Slice/LeaveForm'
import { FiUsers, FiAlertCircle, FiCalendar, FiBarChart2, FiInfo, FiMapPin, FiClock, FiCheckCircle, FiXCircle } from "react-icons/fi";
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { format, isSameDay, addDays, getDay, subDays, parse } from 'date-fns';
import { fetchAllMembers } from '../../../Redux/Slice/AddMember';
import { TablePagination } from "@mui/material";
import socket from '../../../socket';

const GMRequest = () => {
    const dispatch = useDispatch();
    const loginDataStr = Cookies.get("loginData");
    let loginData = null;
    if (loginDataStr) {
        loginData = JSON.parse(loginDataStr);
    }
    const applicantUser = loginData?.data || loginData?.user || {};

    const { leaveData, listLoading, error } = useSelector((state) => state.LeaveForm);
    const { MemberData } = useSelector((state) => state.AddMemberSlice);
    const [filter, setFilter] = useState('All');

    useEffect(() => {

        socket.on("leaveUpdate", (data) => {
            console.log("Live leave update received (GM):", data);
            if (applicantUser?._id) {
                dispatch(getAdvanceLeaves(applicantUser._id));
            }
        });

        return () => {
            socket.off("leaveUpdate");
        };
    }, [dispatch, applicantUser?._id]);

    const role = applicantUser.Role;
    const [chartMode, setChartMode] = useState('Volume');

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Action Modal State
    const [actionModal, setActionModal] = useState({ isOpen: false, id: null, actionType: null });

    const uniqueRoles = [
        ...new Set(
            (MemberData?.data || [])
                ?.map((u) => u?.Role)
                ?.filter((role) => role !== "owner" && role !== "management")
        ),
    ];

    useEffect(() => {
        if (applicantUser?._id) {
            dispatch(getAdvanceLeaves(applicantUser._id));
        }
        dispatch(fetchAllMembers());
    }, [dispatch, applicantUser?._id]);

    const handleAction = (id, action) => {
        setActionModal({ isOpen: true, id, actionType: action });
    };

    const confirmAction = async () => {
        const { id, actionType } = actionModal;
        if (id && actionType) {
            await dispatch(updateLeaveStatus({ id, action: actionType }));
            if (applicantUser?._id) {
                dispatch(getAdvanceLeaves(applicantUser._id));
            }
        }
        setActionModal({ isOpen: false, id: null, actionType: null });
    };

    const cancelAction = () => {
        setActionModal({ isOpen: false, id: null, actionType: null });
    };

    const processedLeaves = leaveData?.map(leave => {
        // Find if this user is in the approvers list
        const userApprover = leave.approvers?.find(app => String(app.approverId) === String(applicantUser?._id));
        // What's their specific status (Pending, Approved, Rejected)
        const userStatus = userApprover ? userApprover.status : leave.Status;

        // Are they the one who is currently supposed to approve?
        const isCurrentApprover = leave.approvers &&
            leave.currentApprovalStep < leave.approvers.length &&
            String(leave.approvers[leave.currentApprovalStep].approverId) === String(applicantUser?._id);

        return {
            ...leave,
            userStatus,
            isCurrentApprover
        };
    }) || [];

    const filteredLeaves = processedLeaves.filter(leave => {
        if (leave.Status === 'Withdrawn') return false;
        // Find the approver instance matching the current user
        const myApproverData = leave.approvers?.find(app => String(app.approverId) === String(applicantUser?._id));

        // IMPORTANT: If they are an approver, but it's not their turn yet,
        // and they haven't made a decision yet, we shouldn't show it for them to act.
        // The prompt says: "pahle firstApprover jayega fir ye secondApprover ke pass Request jaeygi varna show nai hona chahiye"
        // This means we should ONLY include this leave if they have already made a decision (Approved/Rejected)
        // OR if it's currently their turn. 
        if (myApproverData) {
            if (myApproverData.status === 'Pending' && !leave.isCurrentApprover) {
                return false; // Not their turn yet, don't show to them in the list at all
            }
        }

        if (filter === 'All') return true;
        if (filter === 'Approved' && leave.userStatus === 'Approved by Owner') return true;
        return leave.userStatus === filter;
    });

    let teamCount = 0;

    const membersArray =
        MemberData?.data || (Array.isArray(MemberData) ? MemberData : []);

    const validRoles = [
        "Delphi Junior Developer",
        "Web Junior Developer",
        "inhouse",
        "Data Analyst",
        "Junior Customer Support",
        "Senior Customer Support",
        "Web Senior Developer",
        "Delphi Senior Developer",
    ];

    if (Array?.isArray(membersArray)) {
        teamCount = membersArray?.filter(
            (member) =>
                validRoles?.includes(member?.Role) && member?.Profile === "On"
        )?.length;
    }

    let allCount = leaveData?.filter(l => l.Status).length;

    // Find leaves that have been evaluated and fully 'Approved'
    const currentlyAwayCount = processedLeaves.filter(leave => leave.Status === 'Approved').length;

    const teamAvailability = teamCount > 0
        ? Math.round(((teamCount - currentlyAwayCount) / teamCount) * 100)
        : 100;

    // Calculate today's absent count (approved leaves that cover today)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const todayAbsentCount = (leaveData || []).filter(leave => {
        if (leave.Status !== 'Approved') return false;
        if (!leave.fromDate) return false;
        const start = new Date(leave.fromDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(leave.toDate || leave.fromDate);
        end.setHours(0, 0, 0, 0);
        return today >= start && today <= end;
    }).length;

    // Calculate estimated deductions for this month
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    const parseTime = (timeStr) => {
        const referenceDate = new Date('2000-01-01');
        let parsed = parse(timeStr, 'HH:mm', referenceDate);
        if (isNaN(parsed)) parsed = parse(timeStr, 'hh:mm a', referenceDate);
        if (isNaN(parsed)) parsed = parse(timeStr, 'h:mm a', referenceDate);
        if (isNaN(parsed)) parsed = parse(timeStr, 'HH:mm:ss', referenceDate);
        return isNaN(parsed) ? null : parsed;
    };

    const calculateItemDeduction = (item) => {
        if (item.Status === 'Withdrawn' || item.Status === 'Rejected') return 0;

        const salary = parseFloat(item.applicant?.Salary) || 0;
        const dailyRate = salary / 26;
        const hourlyRate = dailyRate / 8.5;

        if (item.LeaveType === 'Half Day Leave') {
            return dailyRate / 2;
        }

        if (['Late Arrival', 'Time Break', 'Early Leave'].includes(item.LeaveType)) {
            if (item.leavingTime && item.arrivalTime) {
                const start = parseTime(item.leavingTime);
                const end = parseTime(item.arrivalTime);
                if (start && end) {
                    const diffHours = (end - start) / (1000 * 60 * 60);
                    return diffHours > 0 ? diffHours * hourlyRate : 0;
                }
                return hourlyRate;
            }
        }

        const days = parseFloat(item.TotalDays) || 1;
        return dailyRate * days;
    };

    const totalDeductions = (leaveData || [])
        .filter(leave => {
            if (!leave.fromDate) return false;
            const leaveDate = new Date(leave.fromDate);
            return leaveDate.getMonth() === currentMonth && leaveDate.getFullYear() === currentYear;
        })
        .reduce((sum, leave) => sum + calculateItemDeduction(leave), 0);

    // 📊 Chart Data Calculations
    const leaveDistribution = (leaveData || [])
        .filter(l => l.Status === 'Approved')
        .reduce((acc, leave) => {
            const type = leave.LeaveType;
            if (type) acc[type] = (acc[type] || 0) + 1;
            return acc;
        }, {});

    const volumeData = [
        { name: "Advance", value: leaveDistribution["Advance Leave"] || 0, type: "Advance Leave" },
        { name: "Emergency", value: leaveDistribution["Emergency Leave"] || 0, type: "Emergency Leave" },
        { name: "Late", value: leaveDistribution["Late Arrival"] || 0, type: "Late Arrival" },
        { name: "Early", value: leaveDistribution["Early Leave"] || 0, type: "Early Leave" },
        { name: "Half Day", value: leaveDistribution["Half Day Leave"] || 0, type: "Half Day Leave" },
        { name: "Break", value: leaveDistribution["Time Break"] || 0, type: "Time Break" },
    ];

    const trendData = [];
    for (let i = 14; i >= 0; i--) {
        const dateObj = subDays(new Date(), i);
        const dateStr = format(dateObj, 'yyyy-MM-dd');
        const count = (leaveData || []).filter(l =>
            l.Status === 'Approved' &&
            format(new Date(l.fromDate), 'yyyy-MM-dd') === dateStr
        ).length;
        trendData.push({
            name: format(dateObj, 'dd MMM'),
            value: count,
            type: "Approved Requests"
        });
    }

    const currentData = chartMode === 'Volume' ? volumeData : trendData;

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-white px-5 py-3 rounded-[24px] shadow-sm border border-gray-100 flex flex-col items-center gap-1.5 min-w-[140px]">
                    <p className="text-[9px] font-black text-blue-900 uppercase tracking-[0.15em] mb-1">
                        {chartMode === 'Volume' ? 'Leave Distribution' : 'Daily Trend'}
                    </p>
                    <div className="h-px w-8 bg-gray-100 mb-1"></div>
                    <p className="text-[13px] font-black tracking-tight text-[#133A69]">
                        {data.type || data.name}: {data.value}
                    </p>
                    <p className="text-[8px] font-bold text-gray-400 opacity-60 mt-1 uppercase">
                        {chartMode === 'Volume' ? 'Total Approved' : data.name}
                    </p>
                </div>
            );
        }
        return null;
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Pending':
                return 'bg-amber-100 text-amber-700 border-amber-200';
            case 'Approved':
            case 'Approved by Owner':
                return 'bg-green-100 text-green-700 border-green-200';
            case 'Rejected':
                return 'bg-red-100 text-red-700 border-red-200';
            default:
                return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    };

    const calculateWorkingDays = (startDate, endDate) => {
        if (!startDate || !endDate) return 0;
        let count = 0;
        let curr = new Date(startDate);
        const end = new Date(endDate);
        while (curr <= end) {
            if (getDay(curr) !== 0) { // 0 is Sunday
                count++;
            }
            curr = addDays(curr, 1);
        }
        return count;
    };

    return (
        <>
            <div className="px-3 py-6 min-h-0 bg-gradient-to-br from-slate-50/50 via-white to-slate-50/20 font-sans">
                {/* Page Title */}
                <div className="title mb-6">
                    <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
                        General Manager Requests
                    </h3>
                </div>

                {/* Top Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
                    {/* Workforce Card */}
                    <div className="bg-white rounded-2xl p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] border border-gray-150 flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-50/70 text-blue-500 flex items-center justify-center shrink-0">
                            <FiUsers className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-slate-700 tracking-wider uppercase mb-0.5">Total Workforce</p>
                            <h2 className="text-2xl font-bold text-gray-900">{teamCount}</h2>
                            <p className="text-xs text-slate-600 font-semibold">{uniqueRoles.length} Active Teams</p>
                        </div>
                    </div>

                    {/* Pending Actions Card */}
                    <div className="bg-white rounded-2xl p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] border border-gray-150 flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-50/70 text-amber-500 flex items-center justify-center shrink-0">
                            <FiAlertCircle className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-slate-700 tracking-wider uppercase mb-0.5">Pending GM Action</p>
                            <h2 className="text-2xl font-bold text-gray-900">
                                {processedLeaves.filter(l => l.userStatus === 'Pending' && l.isCurrentApprover).length}
                            </h2>
                            <p className="text-xs text-slate-600 font-semibold">Awaiting Final Review</p>
                        </div>
                    </div>

                    {/* Current Absences Card */}
                    <div className="bg-white rounded-2xl p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] border border-gray-150 flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-purple-50/70 text-purple-500 flex items-center justify-center shrink-0">
                            <FiCalendar className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-slate-700 tracking-wider uppercase mb-0.5">Current Absences</p>
                            <h2 className="text-2xl font-bold text-gray-900">{todayAbsentCount}</h2>
                            <p className="text-xs text-slate-600 font-semibold">Across Department</p>
                        </div>
                    </div>

                    {/* Deduction Impact Card */}
                    <div className="bg-white rounded-2xl p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] border border-gray-150 flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-rose-50/70 text-rose-500 flex items-center justify-center shrink-0">
                            <FiAlertCircle className="w-5 h-5" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-slate-700 tracking-wider uppercase mb-0.5">Deduction Impact</p>
                            <h2 className="text-2xl font-bold text-rose-600">₹{totalDeductions?.toLocaleString()}</h2>
                            <p className="text-xs text-slate-600 font-semibold">Estimated this month</p>
                        </div>
                    </div>
                </div>

                {/* Main Content Dashboard */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Chart Section */}
                    <div className="lg:col-span-2 bg-white rounded-[14px] border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-6 relative flex flex-col min-h-[450px]">
                        <div className="flex justify-between items-start mb-10">
                            <div>
                                <h2 className="text-[17px] font-bold text-gray-800 tracking-tight">Team Leave Comparison</h2>
                                <p className="text-[13px] text-gray-500 font-medium mt-0.5">Activity volume across department teams</p>
                            </div>
                            <div className="flex items-center bg-gray-50/80 rounded-lg p-1 border border-gray-100">
                                <button
                                    onClick={() => setChartMode('Volume')}
                                    className={`px-4 py-1.5 rounded-md text-[11px] font-bold tracking-wide transition-colors ${chartMode === 'Volume' ? 'bg-white shadow-[0_1px_3px_rgba(0,0,0,0.06)] text-gray-800' : 'text-gray-500'}`}
                                >
                                    Volume
                                </button>
                                <button
                                    onClick={() => setChartMode('Trend')}
                                    className={`px-4 py-1.5 rounded-md text-[11px] font-bold tracking-wide transition-colors ${chartMode === 'Trend' ? 'bg-white shadow-[0_1px_3px_rgba(0,0,0,0.06)] text-gray-800' : 'text-gray-500'}`}
                                >
                                    Trend
                                </button>
                            </div>
                        </div>

                        <div className="w-full h-[250px] sm:h-[300px] lg:h-auto lg:flex-grow relative mt-2 sm:mt-6">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={currentData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#133A69" stopOpacity={0.15} />
                                            <stop offset="95%" stopColor="#133A69" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 500 }} dy={10} interval={chartMode === 'Trend' ? 2 : 0} />
                                    <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#cbd5e1', strokeWidth: 1, strokeDasharray: '4 4' }} />
                                    <Area type="monotone" dataKey="value" stroke="#133A69" strokeWidth={2.5} fillOpacity={1} fill="url(#colorValue)" activeDot={{ r: 5, fill: '#133A69', strokeWidth: 0 }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Right Critical Actions Section */}
                    <div className="bg-white rounded-[14px] border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.02)] flex flex-col min-h-[450px]">
                        <div className="p-5 flex justify-between items-center">
                            <h2 className="text-[15px] font-bold text-gray-800 tracking-tight">Critical Actions</h2>
                            <span className="text-[10px] font-bold text-rose-500 bg-rose-50 px-2 py-0.5 rounded tracking-wide">Live</span>
                        </div>

                        <div className="flex-1 overflow-y-auto px-1 max-h-[400px]" style={{ scrollbarWidth: 'thin' }}>
                            {filteredLeaves.filter(leave => leave.userStatus === 'Pending' && leave.isCurrentApprover).length > 0 ? (
                                filteredLeaves.filter(leave => leave.userStatus === 'Pending' && leave.isCurrentApprover).map((leave, index) => (
                                    <div key={leave._id || index} className="p-4 mx-3 mb-2 rounded-xl border border-transparent hover:border-gray-100 hover:bg-gray-50/50 transition-colors">
                                        <div className="flex justify-between items-start mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-full bg-[#f1f5f9] text-gray-600 flex items-center justify-center font-bold text-[12px] shrink-0">
                                                    {leave.applicantName ? leave.applicantName.charAt(0).toUpperCase() : 'U'}
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-[13px] text-gray-800 tracking-tight">{leave.applicantName || "Unknown User"}</h3>
                                                    <p className="text-[11px] text-gray-400 font-medium">{leave.applicantRole}</p>
                                                </div>
                                            </div>
                                            <span className="text-[9px] font-bold text-amber-600 border border-amber-200 bg-amber-50 px-2 py-0.5 rounded uppercase tracking-wider">
                                                {leave.currentApprovalStep > 0 ? "PM APPROVED" : "PENDING"}
                                            </span>
                                        </div>

                                        <div className="bg-white rounded-xl p-3 mb-3 border border-gray-100 shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
                                            <p className="text-[12px] text-gray-500 italic mb-2">"{leave.reason || 'Leave request awaiting GM final decision.'}"</p>
                                            <p className="text-[10px] font-bold text-indigo-500 flex items-center gap-1.5 uppercase">
                                                <FiClock className="w-3 h-3 text-indigo-400" />
                                                {leave.LeaveType === "Emergency Leave" ? (
                                                    <span className="text-red-500">Emergency ({leave.leaveReason})</span>
                                                ) : leave.LeaveType === "Early Leave" ? (
                                                    `Leaving at: ${leave.leavingTime}`
                                                ) : leave.LeaveType === "Late Arrival" ? (
                                                    `Arriving at: ${leave.arrivalTime}`
                                                ) : leave.LeaveType === "Time Break" ? (
                                                    `Break: ${leave.leavingTime} - ${leave.arrivalTime}`
                                                ) : (
                                                    `${leave.TotalDays !== undefined ? leave.TotalDays : calculateWorkingDays(leave.fromDate, leave.toDate)} Days ${leave.halfType ? `(${leave.halfType})` : ''}`
                                                )}
                                            </p>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleAction(leave._id, 'Approve')}
                                                className="flex-1 bg-[#059669] hover:bg-[#047857] text-white text-[11px] font-bold py-2.5 rounded-lg transition-colors tracking-widest shadow-sm">
                                                APPROVE
                                            </button>
                                            <button
                                                onClick={() => handleAction(leave._id, 'Reject')}
                                                className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 text-[11px] font-bold py-2.5 rounded-lg transition-colors tracking-widest shadow-sm">
                                                REJECT
                                            </button>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="p-8 text-center">
                                    <p className="text-gray-500 text-[13px] font-medium">No pending actions requiring your attention right now.</p>
                                </div>
                            )}
                        </div>

                    </div>
                </div> <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 border-t border-gray-100 pt-8">
                    <h2 className="font-bold text-xl text-gray-800">Recent Applications</h2>

                    <div className="flex bg-white rounded-lg p-1 border border-gray-200 shadow-sm">
                        {['All', 'Pending', 'Approved', 'Rejected'].map((status) => (
                            <button
                                key={status}
                                onClick={() => setFilter(status)}
                                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${filter === status
                                    ? 'bg-[#133A69] text-white shadow-md'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}
                            >
                                {status}
                            </button>
                        ))}
                    </div>
                </div>

                {
                    error && typeof error === 'string' && (
                        <div className="mb-6 bg-red-50 text-red-600 p-4 rounded-lg flex items-center gap-3 border border-red-200">
                            <FiInfo className="w-5 h-5 shrink-0" />
                            <p className="font-medium text-sm">{error}</p>
                        </div>
                    )
                }

                {
                    listLoading ? (
                        <div className="flex flex-col justify-center items-center h-64 gap-4">
                            <div className="w-10 h-10 border-4 border-[#133A69]/20 border-t-[#133A69] rounded-full animate-spin"></div>
                            <p className="text-gray-500 font-medium">Loading requests...</p>
                        </div>
                    ) : filteredLeaves.length === 0 ? (
                        <div className="flex flex-col justify-center items-center h-64 bg-white rounded-xl border border-gray-100 shadow-sm">
                            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                                <FiCalendar className="w-8 h-8 text-gray-400" />
                            </div>
                            <h3 className="text-gray-800 font-bold text-lg">No requests found</h3>
                            <p className="text-gray-500 mt-1">There are no {filter.toLowerCase()} leave requests at the moment.</p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl shadow-[0_2px_10px_rgba(0,0,0,0.04)] border border-gray-100 overflow-hidden w-full">

                            {/* Mobile & Tablet Card View (Hidden on Desktop) */}
                            <div className="lg:hidden divide-y divide-gray-100">
                                {filteredLeaves.map((leave) => (
                                    <div key={leave._id} className="p-4 hover:bg-gray-50/50 transition-colors">
                                        <div className="flex justify-between items-start mb-4">
                                            <div className="flex items-start gap-3">
                                                <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-sm shrink-0 border border-blue-100">
                                                    {leave.applicantName ? leave.applicantName.charAt(0).toUpperCase() : 'U'}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-[14px] text-gray-800 tracking-tight">{leave.applicantName || "Unknown User"}</p>
                                                    <p className="text-[12px] text-gray-500 font-medium mt-0.5">{leave.applicantRole}</p>
                                                </div>
                                            </div>
                                            <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${getStatusStyle(leave.userStatus)}`}>
                                                {leave.userStatus}
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-gray-50/50 rounded-lg border border-gray-100">
                                            <div>
                                                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Leave Type</p>
                                                <p className="text-[12px] font-semibold text-gray-700">{leave.LeaveType}</p>

                                            </div>
                                            <div>
                                                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Duration</p>
                                                <div className="flex flex-col gap-0.5">
                                                    <div className="flex items-center gap-1.5 text-[12px] font-medium text-gray-700">
                                                        <FiCalendar className="w-3 h-3 text-[#133A69]" />
                                                        <span className="truncate">
                                                            {isSameDay(new Date(leave.fromDate), new Date(leave.toDate))
                                                                ? format(new Date(leave.fromDate), 'dd MMM')
                                                                : `${format(new Date(leave.fromDate), 'dd MMM')} - ${format(new Date(leave.toDate), 'dd MMM')}`}
                                                        </span>
                                                    </div>
                                                    <p className="text-[10px] font-bold text-blue-600 block pl-4">
                                                        {leave.LeaveType === "Early Leave" ? (
                                                            `Leaving at: ${leave.leavingTime}`
                                                        ) : leave.LeaveType === "Late Arrival" ? (
                                                            `Arriving at: ${leave.arrivalTime}`
                                                        ) : leave.LeaveType === "Time Break" ? (
                                                            `Break: ${leave.leavingTime} - ${leave.arrivalTime}`
                                                        ) : (
                                                            <>
                                                                {leave.TotalDays !== undefined ? leave.TotalDays : calculateWorkingDays(leave.fromDate, leave.toDate)} {leave.TotalDays === 1 ? 'Day' : 'Days'}
                                                                {leave.halfType && <span className="ml-1 text-gray-400 font-medium">({leave.halfType})</span>}
                                                            </>
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">Reason Detail</p>
                                            <p className="text-[13px] text-gray-600 font-medium line-clamp-2" title={leave.reason}>
                                                {leave.reason}
                                            </p>
                                            <div className="mt-1 flex items-center gap-2">

                                                {leave.outOfStation && (
                                                    <p className="text-[11px] text-orange-600 font-medium mt-1 flex items-center gap-1">
                                                        <FiMapPin className="w-3 h-3" /> {leave.cityName}
                                                    </p>
                                                )}
                                                {leave.LeaveType === "Emergency Leave" && (
                                                    <p className="text-[10px] text-red-500 font-bold mt-1 tracking-tight">({leave.leaveReason})</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pt-3 border-t border-gray-100">
                                            <div className="flex items-center gap-1.5">
                                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></div>
                                                <p className="text-[11px] text-blue-500 font-bold">
                                                    Approval: Step {Math.min(leave.currentApprovalStep + 1, 3)} of 3
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2 w-full sm:w-auto">
                                                {leave.userStatus === 'Pending' && leave.isCurrentApprover ? (
                                                    <>
                                                        <button
                                                            onClick={() => handleAction(leave._id, 'Approve')}
                                                            className="flex-1 flex justify-center items-center gap-1 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-md text-[12px] font-semibold transition-colors shadow-sm outline-none">
                                                            <FiCheckCircle className="w-3.5 h-3.5" />
                                                            Approve
                                                        </button>
                                                        <button
                                                            onClick={() => handleAction(leave._id, 'Reject')}
                                                            className="flex-1 flex justify-center items-center gap-1 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-md text-[12px] font-semibold transition-colors border border-red-200 outline-none">
                                                            <FiXCircle className="w-3.5 h-3.5" />
                                                            Reject
                                                        </button>
                                                    </>
                                                ) : (
                                                    <span className={`px-4 py-1.5 rounded-md text-[12px] font-bold border ${leave.userStatus === 'Approved by Owner' ? 'bg-blue-50 text-blue-600 border-blue-200' : getStatusStyle(leave.userStatus)}`}>
                                                        {leave.userStatus === 'Approved' ? 'Approved' : leave.userStatus === 'Approved by Owner' ? 'Approved by Owner' : leave.userStatus === 'Rejected' ? 'Rejected' : 'Waiting for Others'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Desktop Table View */}
                            <div className="hidden lg:block bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100 relative z-0">
                                <div className="w-full overflow-x-auto" style={{ width: "100%" }}>
                                    <div className="min-w-[1000px] overflow-hidden relative z-0">
                                        <div className="max-h-[600px] overflow-y-auto relative scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                            <table className="min-w-full table-auto border-collapse divide-y divide-gray-200 text-sm">
                                                <thead className="bg-[#133A69] text-white sticky top-0 z-50">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "200px" }}>Employee</th>
                                                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "150px" }}>Team Lead / PM</th>
                                                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "150px" }}>Leave Type</th>
                                                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "180px" }}>Duration</th>
                                                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "250px" }}>Reason Details</th>
                                                        <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "120px" }}>Status</th>
                                                        <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "180px" }}>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100 bg-white">
                                                    {filteredLeaves.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((leave) => (
                                                        <tr key={leave._id} className="group transition-all duration-200 hover:shadow-md hover:bg-gray-50">
                                                            <td className="px-6 py-4 align-top">
                                                                <div className="flex items-center gap-4 mt-1">
                                                                    <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-sm shrink-0 border border-blue-100 overflow-hidden shadow-sm">
                                                                        <img className='w-full h-full object-cover' src={leave?.applicant?.ProfilePhoto} title={leave?.applicant?.Name} alt={leave?.applicantName} />
                                                                    </div>
                                                                    <div>
                                                                        <p className="font-bold text-[13px] text-gray-800 leading-tight group-hover:text-[#133A69] transition-colors">{leave.applicantName || "Unknown User"}</p>
                                                                        <p className="text-[11px] text-gray-500 font-semibold mt-0.5">{leave.applicantRole}</p>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 align-top">
                                                                <span className={`px-2.5 py-1.5 rounded-full text-[12px] font-bold text-gray-700 bg-gray-100 whitespace-nowrap inline-block mt-1 border border-gray-200`}>
                                                                    {leave.approvers[0].name}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 align-top">
                                                                <div className="mt-1">
                                                                    <div className="inline-block bg-gray-50 px-2 py-1 rounded border border-gray-200">
                                                                        <p className="text-[12px] font-semibold text-gray-700">{leave.LeaveType}</p>
                                                                    </div>
                                                                    <div className="mt-1.5 flex flex-col items-start gap-1">
                                                                        {leave.outOfStation && (
                                                                            <p className="text-[11px] text-orange-600 font-bold flex items-center gap-1">
                                                                                <FiMapPin className="w-3 h-3" /> {leave.cityName}
                                                                            </p>
                                                                        )}
                                                                        {leave.LeaveType === "Emergency Leave" && (
                                                                            <p className="text-[11px] text-red-500 font-bold tracking-tight">({leave.leaveReason})</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 align-top">
                                                                <div className="flex flex-col gap-1.5 mt-1">
                                                                    <div className="flex items-center gap-2 text-[12.5px] font-semibold text-gray-700">
                                                                        <FiCalendar className="text-gray-400" size={13} />
                                                                        {isSameDay(new Date(leave.fromDate), new Date(leave.toDate))
                                                                            ? format(new Date(leave.fromDate), 'dd MMM yyyy')
                                                                            : `${format(new Date(leave.fromDate), 'dd MMM')} - ${format(new Date(leave.toDate), 'dd MMM')}`}
                                                                    </div>
                                                                    <p className="text-[11.5px] font-bold text-blue-600 bg-blue-50/50 px-2 py-0.5 rounded inline-block w-fit flex items-center gap-1">
                                                                        {leave.LeaveType === "Early Leave" ? (
                                                                            <span>Leaving at: {leave.leavingTime}</span>
                                                                        ) : leave.LeaveType === "Late Arrival" ? (
                                                                            <span>Arriving at: {leave.arrivalTime}</span>
                                                                        ) : leave.LeaveType === "Time Break" ? (
                                                                            <span>Break: {leave.leavingTime} - {leave.arrivalTime}</span>
                                                                        ) : (
                                                                            <>
                                                                                <span>{leave.TotalDays !== undefined ? leave.TotalDays : calculateWorkingDays(leave.fromDate, leave.toDate)} {leave.TotalDays === 1 ? 'Day' : 'Days'}</span>
                                                                                {leave.halfType && <span className="text-gray-500 font-semibold ml-1">({leave.halfType})</span>}
                                                                            </>
                                                                        )}
                                                                    </p>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 align-top">
                                                                <div className="flex flex-col gap-2 mt-1">
                                                                    <p className="text-[13px] text-gray-600 font-medium leading-relaxed whitespace-pre-wrap bg-gray-50/50 p-2.5 rounded-lg border border-gray-100 min-h-[40px]" title={leave.reason}>
                                                                        {leave.reason || "-"}
                                                                    </p>
                                                                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-blue-500 mt-0.5">
                                                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></div>
                                                                        Step {Math.min(leave.currentApprovalStep + 1, 3)} of 3
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 align-top whitespace-nowrap text-center">
                                                                <span className={`inline-flex px-3 py-1 rounded-full text-[11px] font-bold tracking-wide border shadow-sm mt-1 ${getStatusStyle(leave.userStatus)}`}>
                                                                    {leave.userStatus}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 align-top whitespace-nowrap text-center">
                                                                <div className="flex items-center justify-center gap-2 mt-1">
                                                                    {leave.userStatus === 'Pending' && leave.isCurrentApprover ? (
                                                                        <>
                                                                            <button
                                                                                onClick={() => handleAction(leave._id, 'Reject')}
                                                                                className="px-3 py-1.5 flex items-center gap-1 bg-red-50 hover:bg-red-500 text-red-600 hover:text-white rounded text-[12px] font-semibold transition-all shadow-sm border border-red-200 outline-none">
                                                                                <FiXCircle className="w-3.5 h-3.5" />
                                                                                Reject
                                                                            </button>
                                                                            <button
                                                                                onClick={() => handleAction(leave._id, 'Approve')}
                                                                                className="px-3 py-1.5 flex items-center gap-1 bg-green-50 text-green-600 hover:bg-green-600 hover:text-white rounded text-[12px] font-semibold transition-all shadow-sm border border-green-200 outline-none">
                                                                                <FiCheckCircle className="w-3.5 h-3.5" />
                                                                                Approve
                                                                            </button>
                                                                        </>
                                                                    ) : (
                                                                        <span className={`px-4 py-1.5 rounded border text-[12px] font-semibold shadow-sm ${leave.userStatus === 'Approved by Owner' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-gray-100 border-gray-200 text-gray-500'}`}>
                                                                            {leave.userStatus === 'Approved' ? 'Approved' : leave.userStatus === 'Approved by Owner' ? 'Approved by Owner' : leave.userStatus === 'Rejected' ? 'Rejected' : 'Waiting for Others'}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                                <TablePagination
                                    component="div"
                                    count={filteredLeaves.length}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    className="bg-white border-t border-gray-100 placeholder:text-gray-500 rounded-[0_0_24px_24px]"
                                />
                            </div>
                        </div>
                    )
                }
            </div >

            {/* Action Confirmation Modal */}
            {actionModal.isOpen && (
                <div className="fixed inset-0 bg-gray-900/40 z-[100] flex items-center justify-center p-4 backdrop-blur-[1px]">
                    <div className="bg-white rounded-[16px] p-6 max-w-[380px] w-full shadow-2xl flex flex-col">
                        <h3 className="text-[20px] font-bold text-[#1e3a8a] mb-2 tracking-tight">
                            {actionModal.actionType === 'Approve' ? 'Approve Request' : 'Reject Request'}
                        </h3>

                        <p className="text-[14px] text-gray-600 mb-8 font-medium">
                            Are you sure you want to {actionModal.actionType?.toLowerCase()} this request?
                        </p>

                        <div className="flex gap-4 w-full">
                            <button
                                onClick={cancelAction}
                                className="flex-1 bg-white border border-[#2563eb] text-[#2563eb] font-bold py-2.5 rounded-lg hover:bg-blue-50 transition-colors text-[14px]"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmAction}
                                className={`flex-1 text-white font-bold py-2.5 rounded-lg transition-colors text-[14px] ${actionModal.actionType === 'Approve' ? 'bg-[#10b981] hover:bg-[#059669]' : 'bg-[#dc2626] hover:bg-[#b91c1c]'}`}
                            >
                                Yes, {actionModal.actionType}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default GMRequest;